/* eslint-disable */
import * as React from "react";
import { Box, Button, Step, StepLabel, Stepper, CircularProgress, Typography } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { StepperWrapper } from "./css";
import { analyseAndUpdateBlog, GENERATE_OUTLINE, GENERATE_ARTICLE_FROM_OUTLINE } from "../../../gql/mutations/scrapy";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";

const outlineStructureToHtml = (outline, title) => {
  if (!outline || !Array.isArray(outline)) return "";
  let html = title ? `<h1>${title}</h1>\n<br/>\n` : "";
  html += `<h2>Table of Contents</h2>\n<br/>\n`;
  outline.forEach((section) => {
    html += `<h2>${section.h2}</h2>\n`;
    if (section.h3 && section.h3.length > 0) {
      html += section.h3.map((h3) => `<h3>${h3}</h3>`).join("\n");
    }
    html += "\n<br/>\n";
  });
  return html;
};

export default function ModalStepper({ children, stepsLabel, formData, updateForm, contentQuery, handleClose, refetchContent, setIsDirty, metaDescription }) {
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());
  const [loading, setLoading] = React.useState(false);
  const [loadingMessage, setLoadingMessage] = React.useState("");

  const [generateOutline] = useMutation(GENERATE_OUTLINE);
  const [generateArticleFromOutline] = useMutation(GENERATE_ARTICLE_FROM_OUTLINE);
  const [ANALYSE_BLOG_SCORE] = useMutation(analyseAndUpdateBlog);

  const isStepSkipped = step => skipped.has(step);

  const goToNextStep = () => {
    setActiveStep(prev => Math.min(prev + 1, stepsLabel.length - 1));
  };

  const handleNext = async () => {
    // STEP 0: Generate outline
    if (activeStep === 0) {
      setLoading(true);
      setLoadingMessage("Generating outline…");
      let outlineSuccess = false;
      try {
        const res = await generateOutline({
          variables: {
            data: {
              BlogDescription: [metaDescription, formData.requirements].filter(Boolean).join('\n\n'),
              queryId: contentQuery._id,
              title: formData.title,
              wordCount: formData.wordCount,
            },
          },
        });

        const outlineData = res?.data?.generateOutline;
        const outlineArray = outlineData?.outline || [];
        const outlineHtml = outlineStructureToHtml(outlineArray, formData.title);
        updateForm("outlineHtml", outlineHtml);
        outlineSuccess = true;
      } catch (err) {
        console.log("Outline Error:", err);
        toast.error("Failed to generate outline");
      } finally {
        setLoading(false);
        setLoadingMessage("");
      }

      if (!outlineSuccess) return;

      let newSkipped = skipped;
      if (isStepSkipped(activeStep)) {
        newSkipped = new Set(newSkipped.values());
        newSkipped.delete(activeStep);
      }
      setSkipped(newSkipped);
      goToNextStep();
      return;
    }

    // STEP 1: Generate full article (waits for completion, may take 2–5 minutes)
    if (activeStep === 1) {
      setLoading(true);
      setLoadingMessage("Generating article… This may take 2–5 minutes. Please wait.");
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(formData.outlineHtml, "text/html");
        const headings = doc.querySelectorAll("h1, h2, h3");
        const outlineHeadings = [];
        headings.forEach(h => {
          const text = h.textContent.trim();
          if (!text) return;
          outlineHeadings.push(`${h.tagName.toLowerCase()}: ${text}`);
        });

        const res = await generateArticleFromOutline({
          variables: {
            data: {
              queryId: contentQuery._id,
              title: formData.title,
              BlogDescription: [metaDescription, formData.requirements].filter(Boolean).join('\n\n'),
              outlineHeadings,
              includeImages: formData.includeImages,
              wordCount: formData.wordCount,
            },
          },
        });

        const html = res?.data?.generateArticleFromOutline || "";
        updateForm("articleHtml", html);
        goToNextStep();
      } catch (err) {
        console.log("AI Error:", err);
        toast.error("Failed to generate article");
      } finally {
        setLoading(false);
        setLoadingMessage("");
      }
      return;
    }

    // STEP 2: Analyse + save, then close
    if (activeStep === 2) {
      setLoading(true);
      setLoadingMessage("Saving…");
      try {
        await ANALYSE_BLOG_SCORE({
          variables: {
            data: {
              queryId: contentQuery._id,
              title: formData.title,
              html: formData.articleHtml,
              metaDescription: metaDescription || "",
            },
          },
        });

        if (typeof setIsDirty === "function") setIsDirty(false);
        if (typeof refetchContent === "function") await refetchContent();

        handleClose();
        toast.success("Content updated successfully");
      } catch (err) {
        console.log("Mutation Error:", err);
        toast.error("Server Error");
      } finally {
        setLoading(false);
        setLoadingMessage("");
      }
      return;
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const childrenArray = React.Children.toArray(children);
  const isStep0Invalid = activeStep === 0 && !formData.title.trim();

  return (
    <StepperWrapper>
      <Stepper activeStep={activeStep} connector={null}>
        {stepsLabel.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
            {index < stepsLabel.length - 1 && <ArrowForwardIosIcon className="RightArrow" />}
          </Step>
        ))}
      </Stepper>

      <Box className="BodyWrapper " sx={{ mt: 2, p: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", paddingTop: "150px", height: "100%", width: "100%", gap: 2 }}>
            <CircularProgress />
            {loadingMessage && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 360 }}>
                {loadingMessage}
              </Typography>
            )}
          </Box>
        ) : (
          childrenArray[activeStep]
        )}
      </Box>

      <Box className="FooterWrapper">
        {!loading && (
          <>
            <Button disabled={activeStep === 0 || loading} onClick={handleBack}>
              Back
            </Button>
            <Button onClick={handleNext} disabled={loading || isStep0Invalid}>
              {activeStep === 0
                ? "Continue to outline"
                : activeStep === 1
                ? "Generate article"
                : "Looks good, continue in the editor!"}
            </Button>
          </>
        )}
      </Box>
    </StepperWrapper>
  );
}
